// src/App.jsx

import React, { useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  NavLink,
  useLocation
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { get_category } from './store/reducers/homeReducer';
import { getSwapCount } from './store/reducers/virtualSwapReducer';

// Pages & components
import Home from './pages/Home';
import Shops from './pages/Shops';
import Aboutus from './pages/aboutus';
import Card from './pages/Card';
import Details from './pages/Details';
import Register from './pages/Register';
import Login from './pages/Login';
import Shipping from './pages/Shipping';
import CategoryShops from './pages/CategoryShop';
import SearchProducts from './pages/SearchProducts';
import Payment from './pages/Payment';
import Dashboard from './pages/Dashboard';
import ProtectUser from './utils/ProtectUser';
import Index from './components/dashboard/Index';
import Orders from './components/dashboard/Orders';
import Wishlist from './components/dashboard/Wishlist';
import ChangePassword from './components/dashboard/ChangePassword';
import Order from './components/dashboard/Order';
import Chat from './components/dashboard/Chat';
import ConfirmOrder from './pages/ConfirmOrder';
import BlogPage from './pages/BlogPage';
import BlogDetails from './pages/BlogDetails';
import CompleteProfile from './components/dashboard/Complete_Profile';
import Add_Address from './components/dashboard/Add_Address';
import Search from './pages/Search';
import HyperDelivery from './components/dashboard/HyperDeliveryList'; // ← NEW
import HyperDeliveryDetail from './components/dashboard/HyperDeliveryDetail';
import VirtualSwapPage from './pages/VirtualSwapPage'
import FashnAssetsPage from './pages/FashnAssetsPage'
// Icons for the bottom nav
import {
  FaHome,
  FaStore,
  FaInfoCircle,
  FaRegNewspaper,
  FaLock, FaFemale
} from 'react-icons/fa';

// Google Maps loader
import { LoadScript } from '@react-google-maps/api';

function App() {

  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);

  // Read key from .env
  const mapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  console.log('🔑 Maps key:', mapsKey);
  if (!mapsKey) {
    console.warn('⚠️ missing REACT_APP_GOOGLE_MAPS_API_KEY in .env');
  }

  useEffect(() => {
    dispatch(get_category());
  }, [dispatch]);

  // first letter for dashboard icon
  const userInitial = (
    userInfo?.name?.charAt(0) ||
    userInfo?.email?.charAt(0) ||
    'U'
  ).toUpperCase();

  // for active NavLink styling
  const navItemClass = ({ isActive }) =>
    `flex flex-col items-center ${isActive ? 'text-indigo-600' : 'text-gray-600'
    }`;

  return (
    <LoadScript googleMapsApiKey={mapsKey} libraries={['places']}>
      

      <BrowserRouter>
        <InnerApp
          userInfo={userInfo}
          userInitial={userInitial}
          navItemClass={navItemClass}
        />
      </BrowserRouter>
    </LoadScript>
  );
}

function InnerApp({ userInfo, userInitial, navItemClass }) {
  const location = useLocation();
  const dispatch = useDispatch();
  const { virtual_swap_count } = useSelector((state) => state.virtualSwap);

  // Fetch virtual swap count when user is logged in
  useEffect(() => {
    if (userInfo) {
      dispatch(getSwapCount());
    }
  }, [dispatch, userInfo]);

  return (
    <div className="relative min-h-screen pb-16">
      <Routes>
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<Home />} />
        <Route path="/shops" element={<Shops />} />
        <Route path="/aboutus" element={<Aboutus />} />
        <Route path="/products?" element={<CategoryShops />} />
        <Route path="/products/search?" element={<SearchProducts />} />
        <Route path="/card" element={<Card />} />
        <Route path="/order/confirm?" element={<ConfirmOrder />} />
        <Route path="/shipping" element={<Shipping />} />
        <Route path="/payment" element={<Payment />} />
        <Route path="/product/details/:productId" element={<Details />} />

        <Route path="/dashboard" element={<ProtectUser />}>
          <Route path="" element={<Dashboard />}>
            <Route path="" element={<Index />} />
            <Route path="my-orders" element={<Orders />} />
            <Route path="my-wishlist" element={<Wishlist />} />
            <Route path="order/details/:orderId" element={<Order />} />
            {/* NEW Hyper-Delivery route */}
            <Route path="hyper-delivery" element={<HyperDelivery />} />
            <Route path="hyper-delivery/:orderId" element={<HyperDeliveryDetail />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="chat" element={<Chat />} />
            <Route path="chat/:sellerId" element={<Chat />} />
            <Route path="complete-profile" element={<CompleteProfile />} />
            <Route path="add-address" element={<Add_Address />} />
          </Route>
        </Route>

        <Route path="/blogs" element={<BlogPage />} />
        <Route path="/blog-details/:blogId" element={<BlogDetails />} />
        <Route path="search" element={<Search />} />
        <Route path="/virtual-swap" element={<VirtualSwapPage />} />
        <Route path="/fashnassets" element={<FashnAssetsPage />} />
      </Routes>

      {location.pathname !== '/shops' && (
        <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 shadow-md flex justify-around items-center py-2 z-50">
          <NavLink to="/" end className={navItemClass}>
            <FaHome className="text-xl" />
            <span className="text-xs">Home</span>
          </NavLink>

          <NavLink to="/shops" className={navItemClass}>
            <FaStore className="text-xl" />
            <span className="text-xs">Our Shop</span>
          </NavLink>

          {userInfo ? (
            <NavLink to="/dashboard" className={navItemClass}>
              <div className="w-7 h-6 mb-1 rounded-full flex items-center justify-center text-white font-medium bg-indigo-500">
                {userInitial}
              </div>
              <span className="text-xs">Your Dashboard</span>
            </NavLink>
          ) : (
            <NavLink to="/login" className={navItemClass}>
              <FaLock className="text-xl" />
              <span className="text-xs">Login</span>
            </NavLink>
          )}

          <NavLink to="/virtual-swap" className={navItemClass}>
            <div className="relative">
              <FaFemale className="text-xl" />
              {userInfo && virtual_swap_count > 0 && (
                <div className="absolute -top-[8px] -right-[15px] w-[20px] h-[20px] bg-yellow-500 rounded-full text-white text-xs flex items-center justify-center">
                  {virtual_swap_count}
                </div>
              )}
            </div>
            <span className="text-xs">Swap</span>
          </NavLink>

          <NavLink to="/blogs" className={navItemClass}>
            <FaRegNewspaper className="text-xl" />
            <span className="text-xs">Blog</span>
          </NavLink>
        </div>
      )}
    </div>
  );
}

export default App;
